import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { firebaseAuth, firebaseDb } from '../firebase';

function mapProductDoc(d) {
  const data = d.data();
  return {
    id: d.id,
    _id: d.id,
    ...data,
    thumbnailUrl: data.thumbnailUrl || data.images?.[0] || null,
  };
}

export const productService = {
  getProducts: async ({ category = null, featured = false, search = '', max = 48 } = {}) => {
    try {
      if (!firebaseDb) {
        return { success: false, error: 'Firestore non configuré', products: [] };
      }

      let q = query(
        collection(firebaseDb, 'products'),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc'),
        limit(max)
      );

      if (featured) {
        q = query(
          collection(firebaseDb, 'products'),
          where('isActive', '==', true),
          where('isFeatured', '==', true),
          orderBy('createdAt', 'desc'),
          limit(max)
        );
      } else if (category) {
        q = query(
          collection(firebaseDb, 'products'),
          where('isActive', '==', true),
          where('category', '==', category),
          orderBy('createdAt', 'desc'),
          limit(max)
        );
      }

      const snap = await getDocs(q);
      let products = snap.docs.map(mapProductDoc);

      if (search?.trim()) {
        const term = search.trim().toLowerCase();
        products = products.filter(
          (p) =>
            p.name?.toLowerCase().includes(term) ||
            p.description?.toLowerCase().includes(term) ||
            p.tags?.some((t) => String(t).toLowerCase().includes(term))
        );
      }

      return { success: true, products };
    } catch (error) {
      console.error('getProducts:', error);
      return { success: false, error: error.message, products: [] };
    }
  },

  getProduct: async (productId) => {
    try {
      if (!firebaseDb) return { success: false, error: 'Firestore non configuré' };
      const snap = await getDoc(doc(firebaseDb, 'products', productId));
      if (!snap.exists()) return { success: false, error: 'Produit introuvable' };
      return { success: true, product: mapProductDoc(snap) };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  /** Liste complète (admin/agent). */
  getAllProducts: async () => {
    try {
      if (!firebaseDb) return { success: false, products: [] };
      const q = query(collection(firebaseDb, 'products'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      return { success: true, products: snap.docs.map(mapProductDoc) };
    } catch (error) {
      return { success: false, error: error.message, products: [] };
    }
  },

  createProduct: async (data) => {
    try {
      if (!firebaseAuth?.currentUser || !firebaseDb) {
        return { success: false, error: 'Non authentifié' };
      }
      const images = data.images?.length ? data.images : data.thumbnailUrl ? [data.thumbnailUrl] : [];
      const payload = {
        name: String(data.name || '').trim(),
        description: String(data.description || '').trim(),
        category: data.category || 'autre',
        price: Number(data.price) || 0,
        currency: data.currency || 'USD',
        stock: Math.max(0, Number(data.stock) || 0),
        images,
        thumbnailUrl: data.thumbnailUrl || images[0] || null,
        isActive: data.isActive !== false,
        isFeatured: Boolean(data.isFeatured),
        tags: Array.isArray(data.tags) ? data.tags : [],
        sellerUid: firebaseAuth.currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      if (!payload.name || payload.price <= 0) {
        return { success: false, error: 'Nom et prix valides requis' };
      }
      const ref = await addDoc(collection(firebaseDb, 'products'), payload);
      const snap = await getDoc(ref);
      return { success: true, product: mapProductDoc(snap) };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  updateProduct: async (productId, data) => {
    try {
      if (!firebaseDb) return { success: false, error: 'Firestore non configuré' };
      const updates = { updatedAt: serverTimestamp() };
      ['name', 'description', 'category', 'currency', 'thumbnailUrl', 'isActive', 'isFeatured', 'tags'].forEach(
        (k) => {
          if (data[k] !== undefined) updates[k] = data[k];
        }
      );
      if (data.price !== undefined) updates.price = Number(data.price);
      if (data.stock !== undefined) updates.stock = Math.max(0, Number(data.stock));
      if (data.images !== undefined) updates.images = data.images;

      await updateDoc(doc(firebaseDb, 'products', productId), updates);
      const snap = await getDoc(doc(firebaseDb, 'products', productId));
      return { success: true, product: mapProductDoc(snap) };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  deactivateProduct: async (productId) => {
    return productService.updateProduct(productId, { isActive: false });
  },
};
